import {
  useDataProvider,
  useGetIdentity,
  useGo,
  useInvalidate,
  useNavigation,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import type {
  HitlResolutionInput,
  PipelineItem,
  StageArtifactsRead,
  StageExecutionRead,
  VouchPreview,
} from "@/types/tina";

export interface ResolveHitlVariables {
  resolution?: HitlResolutionInput;
  entity_id?: string;
  resolution_kind?: "entity" | "manual_event_created" | "force_closed";
  // Only meaningful with resolution_kind="force_closed": also terminally close
  // the coalesced same-venue siblings. Omitted → backend defaults to false.
  cascade_siblings?: boolean;
  // Only meaningful with resolution_kind="force_closed": terminally close
  // exactly this selected subset of the representative's coalesced cluster.
  close_event_ids?: string[];
}

interface ResolveHitlOptions {
  navigateOnSuccess?: boolean;
  notifyOnSuccess?: boolean;
  /**
   * Where `navigateOnSuccess` sends the curator. Defaults to the shared HITL
   * queue, which is right for every scrape reason.
   *
   * `enrichment_low_confidence` and `refresh_review` parks live on their own
   * surfaces, so resolving one from `/tina/enrichment` or
   * `/tina/refresh-review` and landing on `/tina/hitl` would drop the curator
   * into a queue their item was never in — `/tina/hitl` hides refresh rows
   * outright.
   *
   * **Two accepted shapes**, told apart by the leading slash:
   *
   * - a **Refine resource name** (`"tina-enrichment"`) → that resource's `list`
   *   route, via `useNavigation().list`. Enough for a surface whose narrowing
   *   is localStorage-backed.
   * - a **router path** (`"/tina/refresh-review?current=3&pageSize=50"`) →
   *   navigated verbatim, query string included. Needed for a Refine `useTable`
   *   surface, whose page and sorters live in the URL under the app's global
   *   `syncWithLocation: true` — a bare route would land the curator on page 1
   *   of a list they were 17 pages into.
   *
   * `hitlQueueReturnTo()` in the panel directory picks the right one per reason.
   */
  navigateTo?: string;
}

/**
 * The success toast's body.
 *
 * `entity_id` is only set by the two resolution kinds that link a Mood entity;
 * a typed `resolution` carries no id at all, so the previous
 * `` `Linked to ${values.entity_id}` `` rendered "Linked to undefined" for every
 * enrichment approve/replace/retry.
 */
function resolveDescription(values: ResolveHitlVariables): string {
  if (values.resolution_kind === "force_closed") {
    return "Closed without a linked entity";
  }
  if (values.entity_id) return `Linked to ${values.entity_id}`;
  return values.resolution
    ? `Applied ${values.resolution.kind}`
    : "Resolution applied";
}

export const useItemsRepository = () => {
  const { custom, getList, getOne } = useDataProvider()("tina");
  const { data: identity } = useGetIdentity<IIdentity>();
  const { open } = useNotification();
  const { list } = useNavigation();
  const go = useGo();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();

  /**
   * Drop every cached view of the queues a resolve just changed.
   *
   * Without this a resolve navigated the curator to a list rendered from the
   * cache that still holds the row they just cleared. `/tina/enrichment`'s
   * `staleTime: 30_000` made it a certainty rather than a race — approve a
   * proposal, land on the queue, and it is still in "Needs review" — but the
   * Refine tables behind `/tina/items` and `/tina/hitl` have the same gap, and
   * so does the dashboard's HITL backlog card.
   *
   * Two caches, because the queues are not all Refine's: the enrichment surface
   * and the metrics cards are plain `useQuery` keys, which
   * `useInvalidate`'s resource-shaped keys never reach.
   *
   * Deliberately not awaited. Invalidation marks the destination's queries stale
   * so they refetch on mount; awaiting the refetches would hold the toast and
   * the navigation behind a network round-trip that has nothing to say.
   */
  function invalidateQueues(eventId: string): void {
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-items",
      id: eventId,
      invalidates: ["list", "detail"],
    });
    // `/tina/refresh-review` is not a `tina-items` table: it reads
    // `GET /refresh-jobs` through the `tina-runs` resource, and so does the count
    // beside its title. Without this the curator lands back on the queue they came
    // from and the row they just cleared is still in it — the same staleness the
    // enrichment key below was added for.
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-runs",
      invalidates: ["list"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["tina", "enrichment-queue"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["tina", "metrics", "hitl-backlog"],
    });
  }

  async function get(id: string): Promise<PipelineItem> {
    const response = await getOne({ resource: "tina-items", id });
    return response as unknown as PipelineItem;
  }

  async function getStages(id: string): Promise<StageExecutionRead[]> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath: `events/${id}/stages` },
    });
    return response.data as StageExecutionRead[];
  }

  async function getStageArtifacts(
    eventId: string,
    stageExecutionId: string,
  ): Promise<StageArtifactsRead> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: {
        urlPath: `events/${eventId}/stages/${stageExecutionId}/artifacts`,
      },
    });
    return response.data as StageArtifactsRead;
  }

  async function listDiscoveries(
    parentEventId: string,
    pageSize = 200,
  ): Promise<{ data: PipelineItem[]; total: number }> {
    const response = await getList!({
      resource: "tina-items",
      pagination: { currentPage: 1, pageSize, mode: "server" as const },
      filters: [
        { field: "parent_event_id", operator: "eq", value: parentEventId },
      ],
      sorters: [],
    });
    return { data: response.data as PipelineItem[], total: response.total };
  }

  async function listCoalesced(
    representativeId: string,
    pageSize = 200,
  ): Promise<PipelineItem[]> {
    const response = await getList!({
      resource: "tina-items",
      pagination: { currentPage: 1, pageSize, mode: "server" as const },
      filters: [
        {
          field: "coalesced_into_event_id",
          operator: "eq",
          value: representativeId,
        },
        { field: "status", operator: "eq", value: "hitl" },
      ],
      sorters: [],
    });
    return response.data as PipelineItem[];
  }

  /**
   * What approving this low-score HITL would also release. Read-only.
   *
   * Deliberately no notification and no Sentry capture on failure: this is a
   * disclosure the panel renders, not an action the curator asked for, so a failed
   * read must stay quiet and let the panel fall back to its cautious copy. The caller
   * treats a rejection as "unknown", which still gates Approve behind the dialog.
   */
  async function getVouchPreview(eventId: string): Promise<VouchPreview> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath: `hitl/${eventId}/vouch-preview` },
    });
    return response.data as VouchPreview;
  }

  async function resolveHitl(
    eventId: string,
    values: ResolveHitlVariables,
    options: ResolveHitlOptions = {},
  ): Promise<PipelineItem> {
    const {
      navigateOnSuccess = true,
      notifyOnSuccess = true,
      navigateTo = "tina-hitl",
    } = options;
    Sentry.addBreadcrumb({
      category: "tina-items.resolve-hitl",
      message: "Resolving HITL",
      level: "info",
      data: {
        eventId,
        entity_id: values.entity_id,
        resolution_kind: values.resolution
          ? values.resolution.kind
          : (values.resolution_kind ?? "entity"),
      },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { ...values, resolved_by: identity?.id },
        meta: { urlPath: `hitl/${eventId}/resolve` },
      });
      const item = response.data as PipelineItem;
      // Before the navigation, so the queue the curator lands on is already
      // known-stale rather than served whole from the cache.
      invalidateQueues(eventId);
      if (notifyOnSuccess) {
        open?.({
          type: "success",
          message:
            values.resolution_kind === "force_closed"
              ? "HITL force closed"
              : "HITL resolved",
          description: resolveDescription(values),
        });
      }
      if (navigateOnSuccess) {
        // A leading slash means the caller handed us a full router path with the
        // list state the curator left behind in its query string; `go` navigates it
        // verbatim, while `list()` can only resolve a resource's bare route.
        if (navigateTo.startsWith("/")) {
          go({ to: navigateTo });
        } else {
          list(navigateTo);
        }
      }
      return item;
    } catch (error) {
      Sentry.captureException(error);
      notifyMutationError(
        open,
        values.resolution_kind === "force_closed"
          ? "Force close HITL"
          : "Resolve HITL",
        error,
      );
      throw error;
    }
  }

  return {
    get,
    getStages,
    getStageArtifacts,
    getVouchPreview,
    listDiscoveries,
    listCoalesced,
    resolveHitl,
  };
};
